<script>
import AkLabel from '@components/v2/general/AkLabel.vue';
import AkSelect from '@components/v2/input/AkSelect.vue';
import utilsMixin from '@mixins/utilsMixin';
import Chart from 'primevue/chart';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import AkDialogHeadless from '@components/general/AkDialogHeadless.vue';

const platformHtmlLegendPlugin = {
  id: 'platformHtmlLegend',
  afterUpdate(chart, args, options) {
    const container = document.getElementById(options?.containerID);
    if (!container) return;

    const items = chart.options.plugins.legend.labels.generateLabels(chart);
    const list = document.createElement('ul');
    list.className = 'rm-legend';
    const listTitle = document.createElement('span');
    listTitle.innerText = options?.title;
    listTitle.className = 'rm-legend-title';
    list.appendChild(listTitle);

    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = `rm-legend__item${item.hidden ? ' rm-legend__item--hidden' : ''}`;
      li.onclick = () => {
        chart.toggleDataVisibility(item.index);
        chart.update();
      };

      const box = document.createElement('span');
      box.className = 'rm-legend__box';
      box.style.background = item.fillStyle;

      const label = document.createElement('span');
      label.textContent = item.text;

      li.appendChild(box);
      li.appendChild(label);
      list.appendChild(li);
    });

    container.innerHTML = '';
    container.appendChild(list);
  },
};

const platformPercentagePlugin = {
  id: 'platformPercentage',
  afterDatasetsDraw(chart) {
    const {ctx} = chart;

    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;

      meta.data.forEach((element, index) => {
        if (!chart.getDataVisibility(index)) return;

        const value = dataset.data[index];
        if (!value || value < 5) return;

        const {x, y} = element.tooltipPosition();

        ctx.save();
        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${value}%`, x, y);
        ctx.restore();
      });
    });
  },
};

export default {
  name: 'SiteDistributionIndicatorCard',
  components: {AkDialogHeadless, AkShimmer, AkSelect, AkLabel, Chart},
  mixins: [utilsMixin],
  props: {
    technicalKey: {type: String, default: 'PLATFORM_DISTRIBUTION'},
    label: {type: String, default: 'Répartition plateforme'},
    legendTitle: {type: String, default: 'Répartition OTA'},
    labelFormatter: {type: Function, default: (label) => label},
  },
  inject: {
    dashboard: {default: null},
    displayMode: {default: 'card'},
  },
  data() {
    return {
      platformHtmlLegendPlugin,
      platformPercentagePlugin,
    };
  },
  computed: {
    isLoading() {
      return (
        (this.dashboard?.indicatorsLoading[this.technicalKey] ?? false) || this.dashboard?.isPreparing.value === true
      );
    },
    indicator() {
      const bareObj = this.dashboard?.indicators[this.technicalKey];
      return bareObj?.indicatorGraphDTO;
    },
    hasError() {
      return !!this.dashboard?.indicatorsError[this.technicalKey] || (!this.isLoading && !this.indicator);
    },
    delta() {
      if (!this.indicator?.main || !this.indicator?.comparison) {
        return null;
      }
      return (((this.indicator.main - this.indicator.comparison) / this.indicator.comparison) * 100).toFixed(1);
    },
    platformChartData() {
      const main = this.indicator?.main;
      if (!main) {
        return {labels: [], datasets: []};
      }
      if (Array.isArray(main.labels) && Array.isArray(main.datasets) && main.datasets.length > 0) {
        return main;
      }
      const entries = Object.entries(main).filter(([, value]) => typeof value === 'number' && !Number.isNaN(value));
      if (!entries.length) {
        return {labels: [], datasets: []};
      }

      const labels = entries.map(([label]) => this.labelFormatter(label));
      const data = entries.map(([, value]) => value);
      const backgroundColor = this.getPieColors(labels.length);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor,
            hoverBackgroundColor: backgroundColor,
            borderWidth: 0,
          },
        ],
      };
    },
    legendId() {
      return `${this.technicalKey}-legend`;
    },
    legendIdFullscreen() {
      return `${this.technicalKey}-legend-fullscreen`;
    },
    isGraph() {
      return this.displayMode === 'graph';
    },
  },
  methods: {
    platformChartOptions(id) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
            position: 'right',
            labels: {
              boxWidth: 18,
              boxHeight: 10,
              padding: 12,
            },
          },
          tooltip: {
            backgroundColor: '#FFF',
            titleColor: '#000',
            bodyColor: '#000',
            displayColors: false,
            borderColor: '#e5e7eb',
            borderWidth: 1,
            callbacks: {
              title: () => '',
              label: (context) => `${context.raw}%`,
            },
          },
          platformHtmlLegend: {
            containerID: id,
            title: this.legendTitle,
            showPercentages: id === this.legendIdFullscreen,
          },
        },
      };
    },
    getPieColors(count) {
      const palette = [
        '#3FB4E5',
        '#FF5A5F',
        '#0049B1',
        '#FFB55B',
        '#A8B0C6',
        '#7ED3A3',
        '#B37FEB',
        '#F7D154',
        '#002D6D',
        '#F58BB8',
      ];
      if (count <= palette.length) {
        return palette.slice(0, count);
      }
      const colors = [];
      for (let i = 0; i < count; i += 1) {
        colors.push(palette[i % palette.length]);
      }
      return colors;
    },
    fullScreen() {
      this.$refs.fullScreen.show();
    },
  },
};
</script>

<template>
  <div v-if="this.indicator?.main" v-bind="$attrs" class="border grow border-grey-100 rounded-xl min-w-[295px] h-fit">
    <div class="p-4 flex flex-col h-full gap-4">
      <template v-if="hasError">
        <div class="w-full flex items-center justify-center flex-col gap-3 p-5 h-full">
          <i
            class="ga-icon ga-warning rounded-full bg-red-50 h-[68px] w-[68px] flex items-center justify-center text-[32px] text-red" />
          <span class="text-red text-center">Une erreur est survenue lors du chargement de cette donnée</span>
        </div>
      </template>
      <div v-else class="flex gap-4">
        <div class="flex gap-2 w-full">
          <div class="flex flex-col w-full gap-4">
            <div class="flex gap-3 items-center w-full">
              <AkLabel class="font-semibold text-grey-900 !mb-0" information="">{{ label }}</AkLabel>
              <button
                @click="fullScreen"
                class="ml-auto w-8 aspect-square flex items-center justify-center border rounded-md border-grey-100 hover:bg-grey-50 hover:border-grey-200">
                <i class="ga-icon ga-arrow-expand text-md text-grey-900" />
              </button>
            </div>
            <div class="flex gap-12 items-center">
              <AkShimmer :loading="isLoading" width="80px" height="42px">
                <Chart
                  :data="platformChartData"
                  type="doughnut"
                  :options="platformChartOptions(legendId)"
                  :plugins="[platformHtmlLegendPlugin]"
                  class="w-[155px] min-w-0" />
                <div :id="legendId" class="flex items-center grow min-w-fit" />
              </AkShimmer>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <AkDialogHeadless
    ref="fullScreen"
    dismissable
    width="800px"
    :show-cancel-button="false"
    content-class="!px-2 md:!px-6"
    class="md:rounded-xl rounded-t-3xl overflow-hidden"
    :show-validate-button="false">
    <span class="px-2 md:px-0 text-[18px] font-medium text-grey-900">{{ label }}</span>
    <div class="dialogGraph flex flex-col md:flex-row gap-12 items-center justify-center">
      <Chart
        :data="platformChartData"
        type="doughnut"
        :options="platformChartOptions(legendIdFullscreen)"
        :plugins="[platformHtmlLegendPlugin, platformPercentagePlugin]"
        class="h-[350px] min-w-0" />
      <div :id="legendIdFullscreen" class="flex items-center min-w-fit" />
    </div>
  </AkDialogHeadless>
</template>

<style scoped>
:deep(.rm-legend) {
  @apply gap-1 flex flex-col list-none;
}

.dialogGraph :deep(.rm-legend) {
  @apply md:flex md:gap-1 gap-2 grid grid-cols-3;
}

:deep(.rm-legend-title) {
  @apply text-grey-900 font-semibold col-span-3 text-center;
}

:deep(.rm-legend__item) {
  @apply text-grey-900 gap-2 text-sm cursor-pointer flex items-center select-none;
}

:deep(.rm-legend__item--hidden) {
  @apply line-through opacity-45;
}

:deep(.rm-legend__box) {
  @apply shrink-0 rounded-[3px] h-[12px] w-[18px] inline-block;
}
</style>
